import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { formatLocalDate } from '../../../../app/utils'
import { NumericFormat } from 'react-number-format'
import { z } from 'zod'
import FormDatePickerInput from '../../../../components/Forms/FormDatePickerInput'
import { Option } from '../../../../components/Forms/FormSelect'
import RequiredAsterisk from '../../../../components/RequiredAsterisk/RequiredAsterisk'
import { SectionLabelTypography } from '../../../../components/Typographies/styles'
import Documents from '../../../Documents/Documents'
import { messages } from './messages'

interface PriceHistoryModalProps {
  tenantId: number
  customerId?: number
  open: boolean
  onClose: () => void
  currencyOptions: Option[]
  initialData?: any
  onSave: (data: any) => void
  selectedCurrencyId?: number
  isViewMode?: boolean
}

const priceHistorySchema = z.object({
  currencyId: z.number({
    required_error: 'Currency is required',
  }),
  price: z
    .number({
      required_error: 'Price is required',
    })
    .min(0.01, 'Price must be greater than 0'),
  priceValidFrom: z.date({
    required_error: 'Valid From date is required',
  }),
  priceValidTo: z.date().optional(),
  name: z.string().optional(),
})

type PriceHistoryFormData = z.infer<typeof priceHistorySchema>

const PriceModal = ({
  tenantId,
  customerId,
  open,
  onClose,
  currencyOptions,
  initialData,
  onSave,
  selectedCurrencyId,
  isViewMode = false,
}: PriceHistoryModalProps) => {
  const { formatMessage } = useIntl()
  const methods = useForm<PriceHistoryFormData>({
    resolver: zodResolver(priceHistorySchema),
    defaultValues: initialData
      ? {
        currencyId: initialData.currencyId,
        price: initialData.price,
        priceValidFrom: initialData.validFrom ? new Date(initialData.validFrom) : new Date(),
        priceValidTo:
          initialData.priceValidTo && initialData.priceValidTo !== '∞'
            ? new Date(initialData.priceValidTo)
            : undefined,
        name: initialData.description || '',
      }
      : {
        currencyId: selectedCurrencyId || undefined,
        price: undefined,
        priceValidFrom: new Date(),
        priceValidTo: undefined,
        name: '',
      },
    mode: 'onChange',
  })
  const {
    control,
    handleSubmit,
    formState: { isValid },
    watch,
  } = methods

  const onSubmit = (data: PriceHistoryFormData) => {
    console.log('Form data before formatting:', data)

    const formattedData = {
      currencyId: data.currencyId,
      price: data.price,
      name: data.name || '',
      priceValidFrom: data.priceValidFrom.toString(),
      priceValidTo: data.priceValidTo ? data.priceValidTo.toString() : '∞',
      validFrom: new Date().toISOString(),
    }

    console.log('Formatted data:', formattedData)
    onSave(formattedData)
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        {isViewMode
          ? formatMessage(messages.viewPrice)
          : initialData
            ? formatMessage(messages.editPrice)
            : formatMessage(messages.addPriceTitle)}
      </DialogTitle>
      <DialogContent>
        <FormProvider {...methods}>
          <form>
            <Box sx={{ mt: 2 }}>
              <Box sx={{ mb: 2 }}>
                <SectionLabelTypography sx={{ mb: 1 }}>
                  {formatMessage(messages.activePrice)} {!isViewMode && <RequiredAsterisk />}
                </SectionLabelTypography>
                {isViewMode ? (
                  <TextField
                    value={
                      initialData?.price?.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }) || ''
                    }
                    fullWidth
                    InputProps={{ readOnly: true }}
                    variant='filled'
                  />
                ) : (
                  <Controller
                    name='price'
                    control={control}
                    render={({ field, fieldState }) => (
                      <NumericFormat
                        customInput={TextField}
                        fullWidth
                        decimalScale={2}
                        thousandSeparator={true}
                        allowNegative={false}
                        onValueChange={(vals) => {
                          field.onChange(vals.floatValue)
                        }}
                        value={field.value}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                )}
              </Box>

              <Box display='flex' columnGap={2} sx={{ mb: 2 }}>
                <Box flex={1}>
                  {isViewMode ? (
                    <>
                      <SectionLabelTypography sx={{ mb: 1 }}>
                        {formatMessage(messages.validFrom)}
                      </SectionLabelTypography>
                      <TextField
                        value={
                          initialData?.validFrom
                            ? formatLocalDate(initialData.validFrom)
                            : ''
                        }
                        fullWidth
                        InputProps={{ readOnly: true }}
                        variant='filled'
                      />
                    </>
                  ) : (
                    <FormDatePickerInput
                      name='priceValidFrom'
                      label={formatMessage(messages.validFrom)}
                      format='YYYY-MM-DD'
                      minDate={'1970/01/01'}
                    />
                  )}
                </Box>
                <Box flex={1}>
                  {isViewMode ? (
                    <>
                      <SectionLabelTypography sx={{ mb: 1 }}>
                        {formatMessage(messages.validTo)}
                      </SectionLabelTypography>
                      <TextField
                        value={
                          initialData?.priceValidTo === '∞'
                            ? '∞'
                            : initialData?.priceValidTo
                              ? formatLocalDate(initialData.priceValidTo)
                              : ''
                        }
                        fullWidth
                        InputProps={{ readOnly: true }}
                        variant='filled'
                      />
                    </>
                  ) : (
                    <FormDatePickerInput
                      name='priceValidTo'
                      label={formatMessage(messages.validTo)}
                      format='YYYY-MM-DD'
                      allowInfinity={true}
                      minDate={'1970/01/01'}
                      infinityLabel='∞'
                    />
                  )}
                </Box>
              </Box>

              <Box sx={{ mb: 2 }}>
                <SectionLabelTypography sx={{ mb: 1 }}>
                  {formatMessage(messages.description)}
                </SectionLabelTypography>
                {isViewMode ? (
                  <TextField
                    value={initialData?.description || ''}
                    fullWidth
                    multiline
                    rows={2}
                    InputProps={{ readOnly: true }}
                    variant='filled'
                  />
                ) : (
                  <Controller
                    name='name'
                    control={control}
                    render={({ field }) => <TextField {...field} fullWidth multiline rows={2} />}
                  />
                )}
              </Box>
              <Box flex={1}>
                <Documents
                  categoryId={3}
                  entityId={initialData?.id || 0}
                  tenantId={tenantId || -1}
                  customerId={customerId}
                  isDisabled={isViewMode}
                />
              </Box>
            </Box>
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {isViewMode ? formatMessage(messages.close) : formatMessage(messages.cancel)}
        </Button>
        {!isViewMode && (
          <Button onClick={handleSubmit(onSubmit)} variant='contained' disabled={!isValid}>
            {initialData ? formatMessage(messages.update) : formatMessage(messages.add)}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default PriceModal
